import type {
  DocumentMetadataUserEditEntry,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditEntry,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import {
  buildDocumentMetadataUserEditPut,
  type BuildDocumentMetadataUserEditPutArgs,
  type DocumentMetadataUserEditPutData,
} from 'features/knowledgeDocumentViewer/utils/buildDocumentMetadataUserEditPut'
import { FAMILY_SHARED_DOCUMENT_METADATA_VALUE_KEYS } from 'features/knowledgeDocumentViewer/utils/documentMetadataFieldKeys'
import { mergeDocumentMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import { buildDocumentMetadataUserEditValues } from 'features/knowledgeDocumentViewer/utils/metadataUserEditWrite'
import type { RolodexEntryPutJson } from 'types'

export type FamilyDocumentMetadataSaveMember = {
  bqDocumentId: string
  systemDocument: SystemDocumentEntry | undefined
  priorEdit: DocumentMetadataUserEditEntry | undefined
  /** Counterpart page for cross-surface cascade on family save. */
  pageNum?: number
  systemPage?: SystemDocumentPageEntry
  pagePriorEdit?: DocumentPageMetadataUserEditEntry
}

export function buildFamilyMemberDocumentMetadataUserValues(
  sharedUserValues: DocumentMetadataUserEditValues,
  systemDocument: SystemDocumentEntry | undefined,
  priorEdit: DocumentMetadataUserEditEntry | undefined
): DocumentMetadataUserEditValues {
  const memberBaseline = mergeDocumentMetadataValues(
    systemDocument?.entry.data,
    priorEdit?.entry.data.values
  )
  const shared = buildDocumentMetadataUserEditValues(sharedUserValues)
  const next = buildDocumentMetadataUserEditValues(memberBaseline)

  for (const key of FAMILY_SHARED_DOCUMENT_METADATA_VALUE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(shared, key)) continue
    next[key] = shared[key] ?? null
  }
  return next
}

export function buildDocumentMetadataUserEditPutsForFamily(args: {
  siteId: string
  organizationId: string
  editedBy: string
  editedAt: string
  sharedUserValues: DocumentMetadataUserEditValues
  members: readonly FamilyDocumentMetadataSaveMember[]
}): RolodexEntryPutJson<DocumentMetadataUserEditPutData>[] {
  return args.members.map((member) => {
    const putArgs: BuildDocumentMetadataUserEditPutArgs = {
      siteId: args.siteId,
      organizationId: args.organizationId,
      bqDocumentId: member.bqDocumentId,
      editedBy: args.editedBy,
      editedAt: args.editedAt,
      systemDocument: member.systemDocument,
      priorEdit: member.priorEdit,
      userValues: buildFamilyMemberDocumentMetadataUserValues(
        args.sharedUserValues,
        member.systemDocument,
        member.priorEdit
      ),
    }
    return buildDocumentMetadataUserEditPut(putArgs)
  })
}
